import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Chatbot from "./Chatbot";
import axios from "../axios";
import "./Home.css";

const Home = () => {
  const [seoData, setSeoData] = useState(null);
  const [showCookieMessage, setShowCookieMessage] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const triggerFireworks = () => {
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 3000);
  };

  // 1️⃣ GET USER LOCATION (GPS/WiFi)
  const getUserLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          const timestamp = new Date().toISOString();

          const locationData = {
            latitude,
            longitude,
            accuracy,
            timestamp
          };

          setUserLocation(locationData);
          console.log("📍 Location obtained:", locationData);

          // 2️⃣ SEND TO BACKEND
          await sendLocationToBackend(locationData);
        },
        (error) => {
          let errorMessage = "";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "User denied location access";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information unavailable";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out";
              break;
            default:
              errorMessage = "Unknown error occurred";
          }
          setLocationError(errorMessage);
          console.error("❌ Location Error:", errorMessage);
        },
        {
          enableHighAccuracy: true, // Use GPS for better accuracy
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setLocationError("Geolocation not supported by browser");
      console.error("❌ Geolocation not supported");
    }
  };

  // 2️⃣ SEND LOCATION TO BACKEND VIA HEADERS
  const sendLocationToBackend = async (locationData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/ping-location/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Lat": locationData.latitude.toString(),
            "X-User-Lon": locationData.longitude.toString(),
            "X-User-Accuracy": locationData.accuracy.toString(),
          },
          body: JSON.stringify({
            timestamp: locationData.timestamp,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Location sent to backend:", data);
      } else {
        console.error("❌ Failed to send location:", response.status);
      }
    } catch (error) {
      console.error("❌ Error sending location to backend:", error);
    }
  };

  useEffect(() => {
    const fetchSeo = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}api/page-details/`
        );
        setSeoData(response.data[0]);
      } catch (error) {
        console.error("Error fetching SEO data:", error);
      }
    };

    fetchSeo();

    const cookieAccepted = localStorage.getItem("cookieAccepted");
    const locationConsent = localStorage.getItem("locationConsent");

    if (!cookieAccepted || !locationConsent) {
      setShowCookieMessage(true);
    } else if (locationConsent === "accepted") {
      // User already consented, get location
      getUserLocation();
    }
  }, []);

  const handleAcceptCookies = () => {
    setShowCookieMessage(false);
    localStorage.setItem("cookieAccepted", "true");
    localStorage.setItem("locationConsent", "accepted");

    // Get location after consent
    getUserLocation();
  };

  const handleRejectCookies = () => {
    localStorage.setItem("locationConsent", "rejected");
    alert("Please accept cookies and location tracking to continue.");
  };

  const handleGetStartedClick = () => {
    const footerElement = document.getElementById("footer-inquiry");
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // 🛡️ Proprietary security stack — used to render the Security Layers section
  const securityLayers = [
    {
      key: "devlock",
      accentClass: "security-navy",
      icon: "🔒",
      name: "DEVLOCK727",
      tagline: "Advanced Security Layer",
      description:
        "Front-end hardening that keeps your application's inner workings out of reach.",
      features: [
        "Unauthorized access prevention on sensitive UI elements",
        "Blocks browser DevTools exploitation",
        "Keeps authentication tokens hidden from exposure",
        "Embedded into platforms like My727ULB Lekha",
      ],
    },
    {
      key: "web727token",
      accentClass: "security-teal",
      icon: "⚡",
      name: "WEB727 Token",
      tagline: "Revolutionary API Security",
      description:
        "Session-first token architecture engineered to eliminate the classic JWT attack surface.",
      features: [
        "Session-based tokens — zero localStorage exposure",
        "50-character cryptographic token strength",
        "IP binding & browser fingerprinting",
        "Rate limiting, TTL expiry & automatic refresh",
      ],
    },
    {
      key: "masu727",
      accentClass: "security-crimson",
      icon: "🛡️",
      name: "MASU727",
      tagline: "Malicious & Suspicious Activity Shield",
      description:
        "Real-time detection and response layer that watches every request, every time.",
      features: [
        "Blocks SQL Injection, XSS & brute-force attempts",
        "Flags suspicious request patterns in real time",
        "Configurable IP-based access control",
        "Enterprise-grade security logging & audit trails",
      ],
    },
  ];

  return (
    <div className="ms-home-container">
      {/* SEO Meta Tags */}
      {seoData && (
        <Helmet>
  <title>{`${String(seoData?.content_object || "My 727 Infotech")} | My 727 Infotech`}</title>

  <meta
    name="description"
    content={String(seoData?.description || "My 727 Infotech")}
  />

  <meta
    name="keywords"
    content={String(seoData?.keywords || "")}
  />

  <meta
    property="og:title"
    content={`${String(seoData?.content_object || "My 727 Infotech")} | My 727 Infotech`}
  />

  <meta
    property="og:description"
    content={String(seoData?.description || "My 727 Infotech")}
  />

  <meta property="og:url" content="https://my727infotech.co.in" />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary_large_image" />

  <script type="application/ld+json">
    {JSON.stringify(seoData?.structured_data || {})}
  </script>
</Helmet>
      )}

      {/* Cookie & Location Consent Message */}
      {showCookieMessage && (
        <div className="cookie-message">
          <div style={{ marginBottom: "10px" }}>
            <p style={{ fontWeight: "bold", marginBottom: "8px" }}>
              🔒 Privacy & Location Consent
            </p>
            <p style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>
              This site uses cookies for secure authentication and tracks your
              GPS location (coordinates and address) for safety monitoring and
              personalized services. By accepting, you consent to location
              tracking as described in our Privacy Policy.
            </p>
          </div>
          <div>
            <button onClick={handleAcceptCookies}>Accept All</button>
            <button onClick={handleRejectCookies}>Reject</button>
          </div>
        </div>
      )}

      {/* Banner Section */}
      <Banner />

      {showFireworks && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: [
                  "#ffd700",
                  "#ff69b4",
                  "#00ffff",
                  "#ff1493",
                  "#00ff00",
                  "#ff4500",
                ][Math.floor(Math.random() * 6)],
                boxShadow: `0 0 20px ${
                  [
                    "#ffd700",
                    "#ff69b4",
                    "#00ffff",
                    "#ff1493",
                    "#00ff00",
                    "#ff4500",
                  ][Math.floor(Math.random() * 6)]
                }`,
                animation: `firework${i % 3} 1.5s ease-out forwards`,
              }}
            />
          ))}
          <style>{`
      @keyframes firework0 {
        0% { transform: translate(0, 0) scale(0); opacity: 1; }
        100% { transform: translate(${Math.random() * 200 - 100}px, ${
            Math.random() * 200 - 100
          }px) scale(3); opacity: 0; }
      }
      @keyframes firework1 {
        0% { transform: translate(0, 0) scale(0); opacity: 1; }
        100% { transform: translate(${Math.random() * 300 - 150}px, ${
            Math.random() * 300 - 150
          }px) scale(2.5); opacity: 0; }
      }
      @keyframes firework2 {
        0% { transform: translate(0, 0) scale(0); opacity: 1; }
        100% { transform: translate(${Math.random() * 250 - 125}px, ${
            Math.random() * 250 - 125
          }px) scale(2); opacity: 0; }
      }
    `}</style>
        </div>
      )}

      {/* Angel Number 727 Section */}
      <section className="angel-number-section">
        <div className="stars-background"></div>
        <div className="container-content">
          <div className="angel-number-badge">
            <div className="number-display">727</div>
          </div>
          <h2 className="section-title">The Power of Angel Numbers</h2>
          <p className="section-description">
            The number <span className="highlight">727</span> reflects the
            beauty of natural alignment — when purpose meets passion as
            effortlessly as rain meets the sky. Our work is built on that same
            harmony:
            <span className="highlight"> trust</span>,
            <span className="highlight"> growth</span>, and
            <span className="highlight"> transformation</span>.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="cosmic-bg"></div>
        <div className="container-content">
          <div className="mission-card">
            <h3 className="mission-title">Our Commitment</h3>
            <p className="mission-text">
              To help businesses harness the power of technology through
              solutions that drive efficiency, enhance user experience, and
              accelerate growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="galaxy-background"></div>
        <div className="container-content">
          <h2 className="section-title">What We Offer</h2>
          <div className="services-grid">
            <div className="service-card service-cyan">
              <div className="service-icon">📱</div>
              <h3>Website & Mobile App Development</h3>
              <p>
                Engaging, responsive, and secure platforms designed to enhance
                digital presence and deliver seamless user experiences.
              </p>
            </div>

            <div className="service-card service-purple">
              <div className="service-icon">📊</div>
              <h3>Data Analytics & Dashboards</h3>
              <p>
                Transform data into actionable insights with dashboards tailored
                to your unique business needs.
              </p>
            </div>

            <div className="service-card service-orange">
              <div className="service-icon">💼</div>
              <h3>Accounting, CRM & ERP Systems</h3>
              <p>
                Integrated solutions that streamline financial operations,
                manage customer relationships, and optimize enterprise
                workflows.
              </p>
            </div>

            <div className="service-card service-gold">
              <div className="service-icon">🏛️</div>
              <h3>e-Governance & ULB ERP Solutions</h3>
              <p>
                Comprehensive platforms like my727ulblekha designed for Urban
                Local Bodies, enabling digital accounting, payroll, and
                transparent fund management.
              </p>
            </div>

            <div className="service-card service-pink">
              <div className="service-icon">🎨</div>
              <h3>UI/UX & Branding Design</h3>
              <p>
                Crafting intuitive designs and brand identities that create a
                lasting impression and elevate customer engagement.
              </p>
            </div>

            <div className="service-card service-indigo">
              <div className="service-icon">🤖</div>
              <h3>AI & Automation Solutions</h3>
              <p>
                Enhance business efficiency through intelligent automation,
                predictive analytics, and AI-driven decision-making tools.
              </p>
            </div>

            <div className="service-card service-red">
              <div className="service-icon">🛡️</div>
              <h3>Cybersecurity & DevTools Protection</h3>
              <p>
                Protect your applications with advanced layers like DEVLOCK727,
                blocking unauthorized access and ensuring production-grade
                security.
              </p>
            </div>

            {/* 💳 FinTech Solutions */}
            <div className="service-card service-green">
              <div className="service-icon">💳</div>
              <h3>FinTech Solutions</h3>
              <p>
                Secure, scalable, and regulatory-compliant financial technology
                platforms designed for payments, digital wallets, and automated
                reconciliation — blending finance and technology with precision.
              </p>
            </div>

            {/* 🔗 ERP Integration & Modern Dashboards */}
            <div className="service-card service-blue">
              <div className="service-icon">🔗</div>
              <h3>ERP Integration & Dashboard Modernization</h3>
              <p>
                Upgrade legacy ERP systems by integrating modern analytics,
                APIs, and custom dashboards — ensuring seamless connectivity,
                enhanced visibility, and real-time decision-making.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Layers Section */}
      <section className="security-section">
        <div className="shield-background"></div>
        <div className="container-content">
          <h2 className="section-title">Our Proprietary Security Stack</h2>
          <p className="section-description security-intro">
            Every platform we build is protected by three layers engineered
            in-house — guarding the front end, the API, and the application
            layer at the same time.
          </p>

          <div className="security-grid">
            {securityLayers.map((layer) => (
              <div
                key={layer.key}
                className={`security-card ${layer.accentClass}`}
              >
                <div className="security-icon">{layer.icon}</div>
                <h3 className="security-name">{layer.name}</h3>
                <p className="security-tagline">{layer.tagline}</p>
                <p className="security-description">{layer.description}</p>
                <ul className="security-feature-list">
                  {layer.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="leadership-section">
        <div className="nebula-background"></div>
        <div className="container-content">
          <h2 className="section-title">Leadership That Inspires</h2>
          <div className="leader-card">
            <div className="leader-name-block" style={{ textAlign: "center" }}>
              <h3
                className="leader-name"
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "700",
                  marginBottom: "6px",
                  color: "#fff",
                  letterSpacing: "0.5px",
                }}
              >
                T.P. Thangaprabhu
              </h3>
              <p
                className="leader-degrees"
                style={{
                  fontSize: "0.95rem",
                  color: "rgba(255, 255, 255, 0.8)",
                  fontWeight: "400",
                  margin: "0",
                  lineHeight: "1.4",
                }}
              >
                B.Sc., MBA., CIMA Adv Dip MA (UK), FCMA., ACPIFSD (IIT-Roorkee)
              </p>
            </div>

            <p className="leader-pronoun">He/Him</p>
            <div className="leader-badges">
              <span className="badge badge-purple">Director</span>
              <span className="badge badge-blue">Software Architect</span>
              <span className="badge badge-cyan">Full-Stack Strategist</span>
            </div>
            <p className="leader-bio">
              A visionary technologist and financial strategist whose expertise
              bridges
              <span className="highlight"> financial accounting</span>,
              <span className="highlight"> financial management</span>,
              <span className="highlight"> data analytics</span>, and
              <span className="highlight"> full-stack development</span>. With a
              strong foundation in finance and technology, Thangaprabhu has led
              transformative projects that unify analytical precision with
              software innovation—empowering businesses to make smarter,
              data-driven decisions.
            </p>

            <p className="leader-bio">
              His leadership blends the discipline of a Cost Accountant with the
              creativity of a Software Architect, crafting solutions that are
              both
              <span className="highlight"> strategically sound</span> and
              <span className="highlight"> technically elegant</span>. Guided by
              his deep belief in the philosophy of <strong>727</strong>—a symbol
              of
              <span className="highlight"> growth</span>,
              <span className="highlight">trust</span>, and
              <span className="highlight">transformative strategy</span>—he
              continues to shape platforms that inspire progress across
              government and enterprise ecosystems.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="universe-background"></div>
        <div className="container-content">
          <h2 className="cta-title">Ready to Transform Your Business?</h2>
          <p className="cta-text">
            Let's create technology that makes an impact today and prepares you
            for tomorrow.
          </p>
          <button
            onClick={triggerFireworks}
            style={{
              display: "block",
              margin: "0 auto",
              padding: "20px 60px",
              fontSize: "1.3rem",
              fontWeight: "700",
              color: "white",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              borderRadius: "50px",
              cursor: "pointer",
              boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-5px) scale(1.05)";
              e.target.style.boxShadow =
                "0 15px 50px rgba(102, 126, 234, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0) scale(1)";
              e.target.style.boxShadow =
                "0 10px 40px rgba(102, 126, 234, 0.4)";
            }}
          >
            Reach Out for Projects
          </button>
        </div>
      </section>

      {/* Footer */}

       {/* Chatbot - Temporarily Disabled */}
      {/* <Chatbot /> */}
    </div>
  );
};

export default Home;